import { useMutation } from "@apollo/client";
import {
  InputControl,
  InputPasswordControl,
  MfaCodeInputForm,
  useAuthenticationPolicy,
  usePlatformConfig,
} from "@aurem/components";
import { Banner, Button, Divider, Text } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContainer } from "components/AuthContainer";
import { ContactSupportLink } from "components/ContactSupportLink";
import GoogleIcon from "components/GoogleIcon";
import ROUTES from "const/routes";
import { useAcceptInvitation } from "hooks/useAcceptInvitation";
import useAnalytics, { AnalyticsEvent } from "hooks/useAnalytics";
import { useNavigateAfterInvitation } from "hooks/useNavigateAfterInvitation";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useFeature } from "providers/FeaturesProvider";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";
import { useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { gql, LoginUserMutation, LoginUserWithMfaCodeMutation } from "types";
import { buildGoogleOAuth2URL, useSignOut } from "utils/auth";
import * as z from "zod";

const SIGN_IN = gql(/* GraphQL */ `
  mutation LoginUser($email: String!, $password: String!) {
    loginUser(email: $email, password: $password) {
      accessToken
      refreshToken
      userId
      isMfaRequired
    }
  }
`);

const SIGN_IN_WITH_MFA = gql(/* GraphQL */ `
  mutation LoginUserWithMfaCode(
    $email: String!
    $password: String!
    $code: String!
  ) {
    loginUserWithMfaCode(email: $email, password: $password, code: $code) {
      refreshToken
      isMfaRequired
      userId
      accessToken
    }
  }
`);

export type Tokens =
  | LoginUserMutation["loginUser"]
  | LoginUserWithMfaCodeMutation["loginUserWithMfaCode"];

const SignIn: FC = () => {
  const { t, tRich, language: i18nLanguage } = useI18n();
  const { language } = useParams();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const { authenticationPolicy } = useAuthenticationPolicy();
  const emailFromParams = searchParams.get("email") ?? "";
  const invitationId = searchParams.get("invitationId") || "";
  const invitationType = searchParams.get("type") || "";
  const invalidInvitation = searchParams.get("invalidInvitation") === "true";
  const [isMfaRequestVisible, setIsMfaRequestVisible] = useState(false);
  const platformConfig = usePlatformConfig();
  const { setAuthState } = useAuth();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { track } = useAnalytics();
  const { acceptInvitation } = useAcceptInvitation();
  const getDestination = useNavigateAfterInvitation();

  const formSchema = z.object({
    email: z
      .string()
      .nonempty(t("apps.employee.validation.email.required"))
      .email({ message: t("apps.employee.validation.email.format") }),
    password: z
      .string()
      .nonempty(t("apps.employee.validation.password.required")),
  });

  const [signIn, { error, loading }] = useMutation(SIGN_IN);
  const [signInWithMfa, { error: errorMfa, loading: loadingMfa }] =
    useMutation(SIGN_IN_WITH_MFA);

  const platformNameKey =
    "apps.admin.pages.controls.branding.platform.signInName" as const;
  const platformName = t(platformNameKey)
    ? tRich(platformNameKey)
    : i18nLanguage === "ar"
      ? platformConfig.nameAr
      : platformConfig.name;

  const isSAMLEnabled = useFeature("saml_feature");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: emailFromParams,
      password: "",
    },
  });

  const signInSuccess = async (tokens: Tokens) => {
    if (!tokens.accessToken || !tokens.refreshToken) {
      setAuthState({
        accessToken: null,
        refreshToken: null,
        status: AuthStatus.Unauthenticated,
      });
      return;
    }

    setAuthState({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      status: AuthStatus.Authenticated,
    });
    track(AnalyticsEvent.SignInByEmail);
    const invitationData = await acceptInvitation({
      accessToken: tokens.accessToken,
    });
    const invitationPath = invitationData
      ? getDestination({ invitationData })
      : undefined;
    const from = invitationPath || state?.from || undefined;
    void navigate(`/${language}`, { replace: true, state: { from } });
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // TODO(Jakub): an explicit signout would probably be better
    await signOut();

    const { data } = await signIn({
      variables: values,
      onError: () => {},
    });
    const tokens = data?.loginUser ?? null;
    if (tokens) {
      if (tokens.isMfaRequired) {
        setIsMfaRequestVisible(true);
      } else {
        void signInSuccess(tokens);
      }
    }
  }

  function handleGoogleSignIn() {
    window.location.href = buildGoogleOAuth2URL({
      action: "signin",
      invitationId,
      invitationType,
      language,
    });
  }

  const submitMfa = async ({
    email,
    password,
    code,
    onError,
  }: {
    email: string;
    password: string;
    code: string;
    onError?: () => void;
  }) => {
    const { data } = await signInWithMfa({
      variables: { email, password, code },
      onError,
    });
    if (!data?.loginUserWithMfaCode) {
      throw new Error(t("apps.admin.pages.signIn.page.signIn.mfa.error"));
    }
    return data.loginUserWithMfaCode;
  };

  return (
    <AuthContainer platform="admin">
      <div>
        {invalidInvitation && (
          <Banner
            color="warning"
            text={t("apps.admin.pages.signIn.page.signIn.invalidInvitation")}
            className="mb-8"
          />
        )}
        <h2 className="font-medium text-2xl">
          {t("apps.admin.pages.signIn.page.signIn.title")} {platformName}
        </h2>
      </div>
      {isMfaRequestVisible && (
        <MfaCodeInputForm
          email={form.getValues().email}
          password={form.getValues().password}
          onSuccess={signInSuccess}
          submit={submitMfa}
          loading={loadingMfa}
          errorMessage={errorMfa?.message}
          onClose={() => setIsMfaRequestVisible(false)}
        />
      )}
      {!isMfaRequestVisible && (
        <FormProvider {...form}>
          <form
            className="flex flex-col gap-6"
            noValidate
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-5">
              <InputControl
                data-e2e-id="signIn_email"
                label={t("apps.admin.pages.signIn.page.signIn.email.label")}
                name="email"
                required
              />
              <InputPasswordControl
                data-e2e-id="signIn_password"
                label={t("apps.admin.pages.signIn.page.signIn.password.label")}
                name="password"
                required
              />
              {(error || errorMfa) && (
                <Text weight="medium" size="sm" className="text-danger-600">
                  {error?.message || errorMfa?.message}
                </Text>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <Button
                data-e2e-id="signIn_button_submit"
                loading={loading}
                type="submit"
              >
                {t("apps.admin.pages.signIn.page.signIn.signIn")}
              </Button>

              {authenticationPolicy?.googleSignInEnabled && (
                <>
                  <Divider text={t("apps.admin.common.or")} />

                  <Button
                    type="button"
                    variant="outlined"
                    color="neutral"
                    data-e2e-id="signIn__button__google"
                    onClick={handleGoogleSignIn}
                    startIcon={<GoogleIcon />}
                  >
                    {t("apps.admin.pages.signIn.page.signIn.signInWithGoogle")}
                  </Button>
                </>
              )}
            </div>
            {isSAMLEnabled && (
              <div className="flex gap-1 justify-center">
                <span className="text-sm text-neutral-800">
                  {t("apps.admin.pages.signIn.page.signIn.youCanAlso")}{" "}
                </span>
                <Button
                  as={Link}
                  to={`/${language}/${ROUTES.SIGN_IN_WITH_SAML}`}
                  variant="text"
                  color="neutral"
                  className="underline"
                >
                  {t("apps.admin.pages.signIn.page.signIn.continueWithSaml")}
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      )}
      {!isMfaRequestVisible && (
        <div className="flex justify-center">
          <Button
            as={Link}
            to={`/${language}/${ROUTES.PASSWORD_FORGOTTEN}`}
            variant="text"
            color="neutral"
            className="underline"
          >
            {t("apps.admin.pages.signIn.page.signIn.forgotPassword")}
          </Button>
        </div>
      )}
      <ContactSupportLink />
    </AuthContainer>
  );
};

export default SignIn;
